import { unitDivisor } from './currency';
import { constructQueryParamName } from './search';

/**
 * Story 8.2 (AC9) — map the tph-api search parser's `parsed_filters` onto the
 * marketplace's `/s` URL query params (the structured filters that ride
 * alongside `aiKeywords`).
 *
 * Posture (LAUNCH approach, locked): map in the marketplace using the
 * listing-field schema it already loads, degrade cleanly, NEVER fabricate. The
 * full original raw query is always preserved in `aiKeywords`, so any dimension
 * that cannot be mapped to an indexed filter is not lost — it stays in the
 * keyword-relevance text. A schema-aware parser upgrade is a future enhancement
 * if telemetry shows misses; not now.
 *
 *   - Deterministic numeric ranges:
 *       price_min_cents / price_max_cents → `price=<min>,<max>` (major units)
 *       birth_year_min / birth_year_max   → `pub_birthYear=<minYear>,<maxYear>`
 *                                           (verbatim — no conversion, no `+1`)
 *       age_min / age_max                 → `pub_birthYear=<minYear>,<maxYear>`
 *                                           (birthYear = currentYear − age), and
 *                                           ONLY when the year dimension did not
 *                                           already claim the param
 *   - Enum dimensions (breed/color/sex → pub_breed/pub_color/pub_sex):
 *       normalize the parser value against the hosted option ids/labels
 *       (case/separator-insensitive). Clean match → set the filter. No confident
 *       match → omit it (the term survives in `aiKeywords`); do not guess.
 *   - intended_uses / intended_use → pub_disciplines (multi-enum `has_any:`):
 *       resolve each through the INTENDED_USE_TO_DISCIPLINE synonym map, then
 *       validate the canonical id against the hosted `disciplines` options
 *       before emitting. Unmapped or unmatched → omitted (kept in `aiKeywords`).
 *   - location → pub_locationState (Story 8.14): a single hosted enum, matched
 *       on option id or label; anything that is not one state or province is
 *       omitted.
 *   - Price: a lower bound alone is emitted too (Story 8.14), anchored at the
 *       plausibility ceiling; an implausible bound omits the dimension.
 *
 * Note: `aiKeywords` itself is added by the caller (route-at-submit), not here.
 */

// Parser dimension → marketplace listing-field key. Only enum dimensions that
// have a confident 1:1 listing field are mapped; others stay in aiKeywords.
const ENUM_DIMENSION_TO_FIELD_KEY = {
  breed: 'breed',
  color: 'color',
  sex: 'sex',
};

// Free-text `intended_use` synonyms → canonical hosted Disciplines option ids.
// Groupings are LOCKED (deferred-work #316, amended 2026-06-12 with the
// discipline's own name: barrelracing/barrelrace): "speed" is intentionally absent
// (stays a loose keyword), and dressage/jumping/trail/ranch/english stay
// unmapped. The hosted `other` option is a catch-all with no synonyms. Keys are
// stored in `normalizeToken` form (lowercase, non-alphanumerics stripped) so
// multi-word synonyms match case/separator-insensitively.
const INTENDED_USE_TO_DISCIPLINE = {
  barrel: 'barrel_racing',
  barrels: 'barrel_racing',
  barrelracing: 'barrel_racing',
  barrelrace: 'barrel_racing',
  '1d': 'barrel_racing',
  '2d': 'barrel_racing',
  '3d': 'barrel_racing',
  '4d': 'barrel_racing',
  barrelracer: 'barrel_racing',
  poles: 'pole_bending',
  polebending: 'pole_bending',
  roping: 'roping',
  rope: 'roping',
  roper: 'roping',
  breakaway: 'roping',
  teamroping: 'roping',
  heading: 'roping',
  heeling: 'roping',
  calfroping: 'roping',
  cutting: 'cutting',
  cutter: 'cutting',
  reining: 'reining',
  reiner: 'reining',
  cowhorse: 'cow_horse',
  boxing: 'cow_horse',
  workingcowhorse: 'cow_horse',
  racing: 'horse_racing',
  horseracing: 'horse_racing',
  race: 'horse_racing',
};

const normalizeToken = value =>
  value == null
    ? ''
    : String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

const findListingField = (config, key) =>
  (config?.listing?.listingFields || []).find(f => f.key === key);

/**
 * Resolve a free-text parser value to a hosted enum option id, matching against
 * both the option id and its human label, case/separator-insensitively.
 *
 * @returns {string|null} the canonical option id, or null when no confident match
 */
const matchEnumOption = (field, value) => {
  const options = field?.enumOptions || field?.options || [];
  const target = normalizeToken(value);
  if (!target) {
    return null;
  }
  const hit = options.find(
    o => normalizeToken(o.option) === target || normalizeToken(o.label) === target
  );
  return hit ? hit.option : null;
};

// Write a validated option id onto the params map, multi-enum aware — the
// single place the `has_any:` prefix is applied.
const setEnumParam = (params, field, optionId) => {
  const paramName = constructQueryParamName(field.key, field.scope || 'public');
  params[paramName] = field.schemaType === 'multi-enum' ? `has_any:${optionId}` : optionId;
};

const isInteger = n => typeof n === 'number' && Number.isFinite(n) && Math.floor(n) === n;

/**
 * Story 8.12 — the foaling-year dimension.
 *
 * 🔴 THE HALF-OPEN INTERVAL, and why there is no `+1` here.
 * `pub_birthYear` is a Sharetribe `long` range filter, half-open `[start, end)`
 * at the API boundary. The marketplace ALREADY compensates for that:
 * `SearchPage.duck.js:114-127` (`constructIntegerRangePropertyForAPI`) adds `+1`
 * to the upper bound, and every read path — `searchListings`,
 * `loadMoreListings`, the SSR `loadData` — funnels through it via
 * `prepareAPIParams` → `prepareIntegerRangeParam`.
 *
 * So this module emits the INCLUSIVE URL form and lets the duck compensate:
 * `pub_birthYear=2020,2023` means 2020..2023 inclusive, and the API is called
 * with `2020,2024`. Adding a second `+1` here would double-compensate and pull
 * in an extra cohort — that is precisely the error that got Story 8.10
 * cancelled. **Do not add one.**
 *
 * ⚠️ NEVER emit an open-ended value. If either endpoint is missing, the duck's
 * `Number.parseInt` yields `NaN`, its `Number.isInteger` guard at `:124` fails,
 * no handler claims the key, and `prepareAPIParams` (`:160`) raw-passes the
 * uncompensated string straight to the API — which silently drops the top year
 * (35 listings, measured on the running site 2026-08-13). A missing endpoint is
 * therefore clamped to a concrete window edge below.
 */

// The plausible-foaling-year window. The floor is the hosted `birthYear` listing
// field's own configured minimum (Sharetribe Console: `long, 1900-2040`), so it
// cannot exclude a real listing. The ceiling is `currentYear + 1`, which admits
// in-utero and next-season foals and is the same window the tph-api V1_4 prompt
// (Rule 4) instructs the parser to classify years against — one definition, used
// by both halves of the story.
const BIRTH_YEAR_FLOOR = 1900;

/**
 * Resolve `birth_year_min`/`birth_year_max` to an inclusive `pub_birthYear`
 * value, or `null` when there is no confident year to emit.
 *
 * Honouring the module posture (`:9-14`): no confident value → omit the param,
 * never guess. A supplied endpoint outside the plausible window is a parse
 * deviation, not a buyer constraint, so it is omitted rather than clamped —
 * clamping it would fabricate a bound the buyer never gave. The raw query
 * survives in `aiKeywords` either way.
 *
 * @param {Object} parsedFilters the `parsed_filters` object
 * @param {number} currentYear the year the ceiling is computed from
 * @returns {string|null} e.g. `'2020,2023'`, or null to emit nothing
 */
const resolveBirthYearRange = (parsedFilters, currentYear) => {
  const rawMin = parsedFilters.birth_year_min;
  const rawMax = parsedFilters.birth_year_max;
  const hasMin = rawMin != null;
  const hasMax = rawMax != null;
  if (!hasMin && !hasMax) {
    return null; // dimension absent (or an untaught/failed parse) → the age path stays live
  }

  const ceiling = currentYear + 1;
  const inWindow = y => isInteger(y) && y >= BIRTH_YEAR_FLOOR && y <= ceiling;
  if ((hasMin && !inWindow(rawMin)) || (hasMax && !inWindow(rawMax))) {
    return null; // non-integer or implausible year → never guess
  }

  // A single-sided range is clamped to the window edge, never left open-ended.
  const min = hasMin ? rawMin : BIRTH_YEAR_FLOOR;
  const max = hasMax ? rawMax : ceiling;
  if (min > max) {
    return null; // inverted range → the API would reject it 400; omit instead
  }

  return `${min},${max}`; // INCLUSIVE. No `+1` — see the note above.
};

/**
 * Story 8.14 AC-1 — the price dimension, lower bound included.
 *
 * `price` is a `min,max` pair, so a lone lower bound ("over $50k") needs an
 * explicit upper anchor. The anchor IS the plausibility ceiling below: high
 * enough that no real listing is excluded, and at or above the price filter's
 * configured max, so AppliedFilterChips labels the chip "Over $50,000".
 *
 * The ceiling: production's highest listed price on 2026-09-21 was $175,000
 * (p99 $125,000, 478 priced of 575 published). $5,000,000 is ~28x that. A bound
 * ABOVE the ceiling is a parse deviation, not a buyer constraint — row 3882
 * "barrel horse over 40kk" parsed a $40,000,000 floor — so, mirroring
 * resolveBirthYearRange, the dimension is OMITTED rather than clamped: clamping
 * would fabricate a bound the buyer never gave, and a $40M floor applied
 * literally would return nothing. An inverted range is omitted too.
 *
 * @returns {string|null} e.g. `'50000,5000000'`, or null to emit nothing
 */
export const PRICE_PLAUSIBLE_MAX_MAJOR = 5000000;

const resolvePriceRange = (parsedFilters, config) => {
  const divisor = unitDivisor(config?.currency) || 100;
  const minCents = parsedFilters.price_min_cents;
  const maxCents = parsedFilters.price_max_cents;
  const hasMin = isInteger(minCents) && minCents >= 0;
  const hasMax = isInteger(maxCents) && maxCents >= 0;
  if (!hasMin && !hasMax) {
    return null;
  }
  const lo = hasMin ? Math.round(minCents / divisor) : 0; // "under $30k" floors to 0
  const hi = hasMax ? Math.round(maxCents / divisor) : PRICE_PLAUSIBLE_MAX_MAJOR;
  if (lo > PRICE_PLAUSIBLE_MAX_MAJOR || hi > PRICE_PLAUSIBLE_MAX_MAJOR || lo > hi) {
    return null; // implausible or inverted → never guess
  }
  return `${lo},${hi}`;
};

/**
 * @param {Object} parsedFilters the `parsed_filters` object returned by /search/route
 * @param {Object} config the marketplace config (config.listing.listingFields, config.currency)
 * @param {Object} [opts]
 * @param {number} [opts.currentYear] override "now" for the age→birthYear conversion (tests)
 * @returns {Object} a flat map of `/s` URL query params (may be empty)
 */
export const mapParsedFiltersToUrlParams = (parsedFilters, config, opts = {}) => {
  const params = {};
  if (!parsedFilters || typeof parsedFilters !== 'object') {
    return params;
  }

  // ── Price: cents → major units (the PriceFilter `price=min,max` param) ──
  const price = resolvePriceRange(parsedFilters, config);
  if (price !== null) {
    params.price = price;
  }

  const currentYear = opts.currentYear || new Date().getFullYear();

  // ── Foaling year → pub_birthYear (Story 8.12), emitted VERBATIM and INCLUSIVE ──
  //
  // 🔴 PRECEDENCE. `pub_birthYear` now has two possible sources — this block and
  // the age block below — and exactly ONE of them may write it. `birth_year_*`
  // wins (collision Rule 3): a four-digit year is absolute and self-dating, an
  // age is relative to a "now" that goes stale, and emitting both would intersect
  // to a provably empty range on a contradicting pair ("born 2020, at least 10
  // years old" → (2020) ∩ (≤2016) = ∅), i.e. a guaranteed zero-result search.
  //
  // The guard below keys on `yearDimensionClaimsParam` — a value derived from the
  // INPUT — rather than on `params.pub_birthYear`, which would be a read of
  // mutation state and would make correctness depend on the order these two
  // blocks happen to appear in. The failure mode that guards against is an
  // order-dependent silent overwrite, invisible to any test that supplies only
  // one of the two dimensions.
  const birthYearRange = resolveBirthYearRange(parsedFilters, currentYear);
  const yearDimensionClaimsParam = birthYearRange !== null;
  if (yearDimensionClaimsParam) {
    params.pub_birthYear = birthYearRange;
  }

  // ── Age → birthYear range (birthYear = currentYear − age) ──
  const ageMin = parsedFilters.age_min;
  const ageMax = parsedFilters.age_max;
  const hasAgeMin = isInteger(ageMin) && ageMin >= 0;
  const hasAgeMax = isInteger(ageMax) && ageMax >= 0;
  if (!yearDimensionClaimsParam && (hasAgeMin || hasAgeMax)) {
    // Younger age → larger birthYear; older age → smaller birthYear.
    const youngestAge = hasAgeMin ? ageMin : 0;
    const oldestAge = hasAgeMax ? ageMax : 50; // sane upper bound when only a floor is given
    const minBirthYear = currentYear - oldestAge;
    const maxBirthYear = currentYear - youngestAge;
    params.pub_birthYear = `${minBirthYear},${maxBirthYear}`;
  }

  // ── Enum dimensions: normalize against the hosted option ids/labels ──
  Object.entries(ENUM_DIMENSION_TO_FIELD_KEY).forEach(([dimension, fieldKey]) => {
    const value = parsedFilters[dimension];
    if (value == null) {
      return;
    }
    const field = findListingField(config, fieldKey);
    if (!field) {
      return;
    }
    const optionId = matchEnumOption(field, value);
    if (!optionId) {
      return; // no confident match → stays in aiKeywords, never guessed
    }
    setEnumParam(params, field, optionId);
  });

  // ── Location (Story 8.14 AC-2, #617) → pub_locationState ──
  // One hosted enum, matched on option id OR label ("Texas", "texas", "TX" →
  // tx). A multi-region ask ("California or Arizona"), a country ("Canada")
  // or a town ("Decatur Texas") matches no single option and is OMITTED — one
  // enum cannot hold two states, and a town is not a state. Never guessed.
  const location = parsedFilters.location;
  const locationField = findListingField(config, 'locationState');
  if (locationField && typeof location === 'string') {
    const optionId = matchEnumOption(locationField, location.trim());
    if (optionId) {
      setEnumParam(params, locationField, optionId);
    }
  }

  // ── Disciplines: synonym hop → canonical Disciplines ids, then the same
  // live-config validation + emission as any enum dimension ──
  //
  // Story 8.14 (ID-10): V1_6 carries EVERY discipline in the new
  // `intended_uses` array and keeps `intended_use` as the single primary
  // string. Read the array, then the primary string — permanently, because
  // pre-V1_6 rows never carry the array. Each value is resolved on its own;
  // the multi-enum emits `has_any:a,b` (either discipline), deduplicated in
  // the parser's order.
  //
  // The primary is APPENDED, not replaced (review L3). V1_6 puts the primary
  // in the array, so a normal parse is unchanged by the dedupe below; on a
  // model deviation whose array resolves to nothing, a valid `intended_use`
  // survives instead of being thrown away.
  const disciplinesField = findListingField(config, 'disciplines');
  if (disciplinesField) {
    const uses = [
      ...(Array.isArray(parsedFilters.intended_uses) ? parsedFilters.intended_uses : []),
      parsedFilters.intended_use,
    ];
    const optionIds = [];
    uses.forEach(use => {
      const disciplineId = INTENDED_USE_TO_DISCIPLINE[normalizeToken(use)];
      const optionId = disciplineId ? matchEnumOption(disciplinesField, disciplineId) : null;
      if (optionId && !optionIds.includes(optionId)) {
        optionIds.push(optionId);
      }
    });
    if (optionIds.length > 0) {
      // A single-value enum can hold only one; the multi-enum takes them all.
      const value =
        disciplinesField.schemaType === 'multi-enum' ? optionIds.join(',') : optionIds[0];
      setEnumParam(params, disciplinesField, value);
    }
  }

  return params;
};

// Keys this module may write to the URL — used by the caller to strip stale
// AI-derived params before applying a fresh search.
export const AI_DERIVED_PARAM_KEYS = [
  'price',
  'pub_birthYear',
  'pub_breed',
  'pub_color',
  'pub_sex',
  'pub_disciplines',
  // Story 8.14 (ID-8) — without this a location from one search would persist
  // into the next and silently narrow an unrelated query.
  'pub_locationState',
  // Story 8.18 (ID-6) — the listing-tag filter, written by searchSubmit.js.
  'pub_proseTags',
];
